import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'

import { getPromotionById, getPromotions } from '@/actions'
import { pageTypes } from '@/constants/common'
import { PageType } from '@/types/servicesTypes/pageTypes'

export const usePagePromotionTokens = (page?: PageType) => {
  const [promotionTokens, setPromotionTokens] = useState<Nullable<number>>(null)
  const { isRegisteredUser } = useAuth()

  const isTabTemplate = page?.pageType === pageTypes.TABS

  const hasPartners = isTabTemplate && !!page?.partnerIds?.length

  const partnerIds = useMemo(
    () =>
      hasPartners
        ? page.partnerIds
            ?.map((partner) => partner.id)
            .filter((item) => item !== undefined)
        : [],
    [page?.partnerIds, hasPartners],
  )

  const promotionId = hasPartners ? page.promotionId : ''

  const getPromotionsData = useCallback(
    async (props: {
      partnerIds: Array<string>
      promotionId?: string
      abortSignal: AbortSignal
    }) => {
      try {
        const { partnerIds, promotionId, abortSignal } = props
        let promotionTokens
        if (promotionId) {
          const promotion = await getPromotionById(promotionId, abortSignal)
          promotionTokens = promotion.wallet?.availableCreditCount
        } else {
          const promotions = await getPromotions(partnerIds, abortSignal)
          promotionTokens = promotions.items.reduce(
            (acc, item) => acc + (item.wallet?.availableCreditCount || 0),
            0,
          )
        }

        setPromotionTokens(promotionTokens ?? null)
      } catch (e) {
        setPromotionTokens(null)
      }
    },
    [promotionId, partnerIds, isRegisteredUser],
  )

  useEffect(() => {
    const controller = new AbortController()

    if (isRegisteredUser && partnerIds?.length) {
      getPromotionsData({
        partnerIds,
        promotionId,
        abortSignal: controller.signal,
      })
    } else {
      setPromotionTokens(null)
    }

    return () => {
      controller.abort()
    }
  }, [partnerIds, promotionId, isRegisteredUser])

  return promotionTokens
}
